import { Controller } from "@hotwired/stimulus"

// Sprint 8 (01/06/2026) — Resize colonnes Excel-like sur tableau supports_b2c.
//
// Usage HTML :
//   <div data-controller="column-resize" data-column-resize-storage-key-value="supports_b2c">
//     <table>
//       <thead><tr><th data-col-id="contact">…</th>…</tr></thead>
//     </table>
//   </div>
//
// Comportement :
// - Ajoute un handle 6px à droite de chaque <th> (sauf colspan=group row)
// - Drag = redimensionne la colonne (min 60px)
// - Largeur persistée en localStorage par data-col-id
// - Restauré au reload
export default class extends Controller {
  static values = { storageKey: String }

  connect() {
    this.startX = 0
    this.startWidth = 0
    this.activeTh = null
    this.activeHandle = null
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)

    this.injectHandles()
    this.restoreWidths()
  }

  injectHandles() {
    const thead = this.element.querySelector("table thead")
    if (!thead) return
    // Cible la dernière ligne de thead (= ligne des colonnes, pas celle des groupes)
    const headerRow = thead.querySelector("tr:last-child")
    if (!headerRow) return

    headerRow.querySelectorAll("th").forEach((th, index) => {
      if (!th.dataset.colId) th.dataset.colId = `col-${index}`
      if (th.querySelector(".col-resize-handle")) return
      const handle = document.createElement("div")
      handle.className = "col-resize-handle"
      handle.addEventListener("mousedown", (e) => this.startResize(e, th, handle))
      th.appendChild(handle)
    })
  }

  startResize(event, th, handle) {
    event.preventDefault()
    event.stopPropagation()
    this.activeTh = th
    this.activeHandle = handle
    this.startX = event.pageX
    this.startWidth = th.offsetWidth
    handle.classList.add("is-resizing")
    document.addEventListener("mousemove", this.onMouseMove)
    document.addEventListener("mouseup", this.onMouseUp)
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
  }

  onMouseMove(event) {
    if (!this.activeTh) return
    const dx = event.pageX - this.startX
    const newWidth = Math.max(60, this.startWidth + dx)
    this.activeTh.style.width = `${newWidth}px`
    this.activeTh.style.minWidth = `${newWidth}px`
    this.activeTh.style.maxWidth = `${newWidth}px`
  }

  onMouseUp() {
    if (!this.activeTh) return
    const colId = this.activeTh.dataset.colId
    const width = this.activeTh.offsetWidth
    this.saveWidth(colId, width)
    this.activeHandle?.classList.remove("is-resizing")
    this.activeTh = null
    this.activeHandle = null
    document.removeEventListener("mousemove", this.onMouseMove)
    document.removeEventListener("mouseup", this.onMouseUp)
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
  }

  saveWidth(colId, width) {
    if (!colId) return
    try {
      const storeKey = `colWidths:${this.storageKeyValue || "default"}`
      const store = JSON.parse(localStorage.getItem(storeKey) || "{}")
      store[colId] = width
      localStorage.setItem(storeKey, JSON.stringify(store))
    } catch (e) {
      console.warn("[column-resize] localStorage save failed", e)
    }
  }

  restoreWidths() {
    try {
      const storeKey = `colWidths:${this.storageKeyValue || "default"}`
      const store = JSON.parse(localStorage.getItem(storeKey) || "{}")
      const headerRow = this.element.querySelector("table thead tr:last-child")
      if (!headerRow) return
      headerRow.querySelectorAll("th").forEach((th) => {
        const colId = th.dataset.colId
        if (colId && store[colId]) {
          const w = `${store[colId]}px`
          th.style.width = w
          th.style.minWidth = w
          th.style.maxWidth = w
        }
      })
    } catch (e) {
      console.warn("[column-resize] localStorage restore failed", e)
    }
  }
}
